import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase"; // Import the initialized supabase client
import { posthog } from "@/lib/posthog";

export const Route = createFileRoute("/auth/callback")({
  component: AuthCallback,
});

function AuthCallback() {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const redirected = useRef(false);

  useEffect(() => {
    // If the provider returned an explicit error in the URL, show it immediately.
    const url = new URL(window.location.href);
    const errDesc =
      url.searchParams.get("error_description") ??
      (window.location.hash.startsWith("#")
        ? new URLSearchParams(window.location.hash.slice(1)).get("error_description")
        : null);
    if (errDesc) {
      posthog.capture("auth_callback_error", { message: errDesc, source: "url_param" });
      setError(errDesc);
      setLoading(false);
      return;
    }

    const nextParam = url.searchParams.get("next");
    // Require exactly one leading slash to block protocol-relative redirects
    // like //evil.com which startsWith("/") but redirect off-domain.
    const redirectDest =
      nextParam && /^\/[^/]/.test(nextParam) ? nextParam : "/alertas";

    // Guard against double-redirect: getSession() and onAuthStateChange can
    // both fire with a valid session; only the first one should navigate.
    const goNext = () => {
      if (redirected.current) return;
      redirected.current = true;
      posthog.capture("auth_callback_redirecting", { dest: redirectDest });
      window.location.href = redirectDest;
    };

    // Fast path: PKCE exchange may have completed before this effect ran.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) goNext();
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session) {
        goNext();
      } else if (event === "INITIAL_SESSION" && session) {
        // Already authenticated when the page loaded.
        goNext();
      } else if (event === "SIGNED_OUT") {
        posthog.capture("auth_callback_error", { message: "SIGNED_OUT", source: "auth_state" });
        setError("Sessão inválida ou expirada. Por favor, faça login novamente.");
        setLoading(false);
      }
      // INITIAL_SESSION with null session is expected while the PKCE exchange
      // is in flight — not an error, keep the spinner.
    });

    // Safety net: stop the spinner if the exchange hangs or produces no event.
    const timeout = setTimeout(() => {
      posthog.capture("auth_callback_error", { message: "timeout_15s", source: "timeout" });
      setError("A validação demorou demais. Verifique se o link não expirou e tente novamente.");
      setLoading(false);
    }, 15_000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center text-foreground">
      <div className="text-center">
        {loading ? (
          <p className="text-sm text-muted-foreground">Validando sua sessão...</p>
        ) : error ? (
          <>
            <p className="text-sm text-red-400">{error}</p>
            <a href="/login" className="mt-4 inline-block text-sm text-primary underline">
              Tentar novamente
            </a>
          </>
        ) : null}
      </div>
    </div>
  );
}
